import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AdminLayout } from '../../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { useToast } from '../../../hooks/useToast';
import { ArrowLeft, Package, Truck, CheckCircle, Clock, AlertTriangle, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import useCheckPermissions from '../../../hooks/useCheckPermissions';

interface ProductImage {
  image_url: string;
}

interface Product {
  title: string;
  price: number;
  images?: ProductImage[];
}

interface OrderItem {
  id: number;
  order_id: number;
  product_id: number;
  quantity: number;
  price: number;
  price_at_purchase: number;
  title?: string;
  image_url?: string;
  product?: Product;
}

interface HistoryEntry {
  date: string;
  old_status: string;
  new_status: string;
  old_payment_status: string;
  new_payment_status: string;
  user_id: string;
  comment: string;
}

interface Order {
  id: string;
  created_at: string;
  status: string;
  payment_status: string;
  customer_name: string;
  customer_email: string;
  total_amount: number;
  items?: OrderItem[];
  history?: HistoryEntry[];
}

// Structure de base du composant
const AdminOrderDetails: React.FC = () => {
  useCheckPermissions(['admin']);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [order, setOrder] = useState<Order | null>(null);
  const [comment, setComment] = useState<string>("");
  
  // États pour la pagination et le filtrage de l'historique
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const itemsPerPage = 5;
  
  // Récupérer les détails de la commande
  const fetchOrderDetails = async () => {
    setIsLoading(true);
    
    try {
      // Récupérer les informations de la commande
      const { data: orderData, error: orderError } = await supabase
        .from('shop_orders')
        .select('*')
        .eq('id', id)
        .single();
      
      if (orderError) throw orderError;
      
      if (orderData) {
        // Récupérer les articles de la commande avec leurs relations produits et images
        const { data: itemsData, error: itemsError } = await supabase
          .from('shop_order_items')
          .select(`
            *,
            product:product_id(title, price, images:shop_product_images(image_url))
          `)
          .eq('order_id', id);
        
        if (itemsError) throw itemsError;
        
        // Mettre à jour les données de la commande
        setOrder({
          ...orderData,
          items: itemsData || []
        });
      } else {
        toast({
          title: 'Commande introuvable',
          description: 'Impossible de trouver les détails de cette commande',
          variant: 'destructive'
        });
        navigate('/admin/boutique/commandes');
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des détails de la commande:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de récupérer les détails de la commande',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  useEffect(() => {
    if (id) {
      fetchOrderDetails();
    }
  }, [id]);
  
  // Fonction pour obtenir la couleur du badge en fonction du statut
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending_payment':
        return 'bg-yellow-500/20 text-yellow-500 hover:bg-yellow-500/30';
      case 'paid':
        return 'bg-green-500/20 text-green-500 hover:bg-green-500/30';
      case 'processing':
        return 'bg-blue-500/20 text-blue-500 hover:bg-blue-500/30';
      case 'shipped':
        return 'bg-purple-500/20 text-purple-500 hover:bg-purple-500/30';
      case 'delivered':
        return 'bg-green-500/20 text-green-500 hover:bg-green-500/30';
      case 'cancelled':
        return 'bg-[#D4AF37]/20 text-[#D4AF37] hover:bg-[#D4AF37]/30';
      default:
        return 'bg-gray-500/20 text-gray-500 hover:bg-gray-500/30';
    }
  };
  
  // Fonction pour obtenir l'icône du statut
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending_payment':
        return <Clock className="h-5 w-5" />;
      case 'paid':
        return <CheckCircle className="h-5 w-5" />;
      case 'processing':
        return <Package className="h-5 w-5" />;
      case 'shipped':
        return <Truck className="h-5 w-5" />;
      case 'delivered':
        return <CheckCircle className="h-5 w-5" />;
      case 'cancelled':
        return <AlertTriangle className="h-5 w-5" />;
      default:
        return <Package className="h-5 w-5" />;
    }
  };
  
  // Fonction pour mettre à jour le statut de la commande
  const updateOrderStatus = async (newStatus: string) => {
    let confirmationMsg = '';
    switch (newStatus) {
      case 'cancelled':
        confirmationMsg = "Voulez-vous vraiment annuler cette commande ?";
        break;
      case 'inquiry':
        confirmationMsg = "Mettre cette commande en attente d’informations ?";
        break;
      case 'pending_payment':
        confirmationMsg = "Repasser cette commande en attente de paiement ?";
        break;
      case 'paid':
        confirmationMsg = "Marquer cette commande comme payée ?";
        break;
      case 'processing':
        confirmationMsg = "Marquer cette commande comme en traitement ?";
        break;
      case 'shipped':
        confirmationMsg = "Marquer cette commande comme expédiée ?";
        break;
      case 'delivered':
        confirmationMsg = "Marquer cette commande comme livrée ?";
        break;
      default:
        confirmationMsg = "Confirmer cette action ?";
    }
    if (!window.confirm(confirmationMsg)) return;
    try {
      // Préparer l'entrée d'historique
      const historyEntry = {
        date: new Date().toISOString(),
        old_status: order?.status || '',
        new_status: newStatus,
        old_payment_status: order?.payment_status || '',
        new_payment_status: order?.payment_status || '',
        user_id: 'admin', // Idéalement récupérer l'ID utilisateur actuel
        comment: comment
      };
      
      // Mettre à jour la commande avec le nouveau statut et ajouter l'entrée d'historique
      const { error } = await supabase
        .from('shop_orders')
        .update({ 
          status: newStatus,
          admin_comment_tmp: comment
        })
        .eq('id', id);
      
      if (error) throw error;
      
      toast({
        title: 'Statut mis à jour',
        description: 'Le statut de la commande a été mis à jour avec succès',
      });
      
      // Réinitialiser le commentaire
      setComment('');
      
      // Actualiser les détails de la commande
      fetchOrderDetails();
    } catch (error) {
      console.error('Erreur lors de la mise à jour du statut:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de mettre à jour le statut de la commande',
        variant: 'destructive'
      });
    }
  };

  // Fonction pour supprimer définitivement la commande
  const deleteOrder = async () => {
    if (!window.confirm("Voulez-vous vraiment supprimer définitivement cette commande ? Cette action est irréversible.")) return;
    try {
      // Supprimer les items liés
      const { error: itemsError } = await supabase
        .from('shop_order_items')
        .delete()
        .eq('order_id', id);
      if (itemsError) throw itemsError;
      // Supprimer la commande
      const { error } = await supabase
        .from('shop_orders')
        .delete()
        .eq('id', id);
      if (error) throw error;
      toast({
        title: 'Commande supprimée',
        description: 'La commande a été supprimée définitivement.',
      });
      navigate('/admin/boutique/commandes');
    } catch (error) {
      console.error('Erreur lors de la suppression de la commande:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de supprimer la commande',
        variant: 'destructive'
      });
    }
  };
  
  // Filtrer et trier l'historique
  const filteredAndSortedHistory = useMemo(() => {
    if (!order?.history) return [];
    
    // Copier l'historique pour le manipuler
    let history = [...order.history];
    
    // Filtrer par statut si un filtre est sélectionné
    if (statusFilter) {
      history = history.filter(entry => entry.new_status === statusFilter);
    }
    
    // Filtrer par terme de recherche dans les commentaires
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      history = history.filter(entry => 
        (entry.comment && entry.comment.toLowerCase().includes(searchLower)) ||
        entry.new_status.toLowerCase().includes(searchLower)
      );
    }
    
    // Trier par date (du plus récent au plus ancien)
    return history.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [order?.history, statusFilter, searchTerm]);
  
  // Paginer l'historique
  const filteredHistory = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredAndSortedHistory.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredAndSortedHistory, currentPage, itemsPerPage]);

  
  return (
    <AdminLayout>
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6">
          <Button 
            variant="outline" 
            onClick={() => navigate('/admin/boutique/commandes')}
            className="mb-4"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour aux commandes
          </Button>
          
          <h1 className="text-2xl font-bold mb-2 text-white">Détails de la commande</h1>
          <p className="text-gray-200">Gérez les détails et le statut de cette commande</p>
        </div>
        
        {isLoading ? (
          <Card className="bg-card border-white/5">
            <CardContent className="pt-6">
              <div className="flex justify-center items-center h-40">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
              </div>
            </CardContent>
          </Card>
        ) : order ? (
          <>
            <Card className="bg-card border-white/5 mb-6">
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-xl text-white">
                      Commande #{order.order_number}
                    </CardTitle>
                    <p className="text-white/70 mt-1">
                      Passée le {new Date(order.created_at).toLocaleDateString('fr-FR')} à {new Date(order.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                  <Badge className={getStatusColor(order.status)}>
                    <span className="flex items-center">
                      {getStatusIcon(order.status)}
                      <span className="ml-1">
                        {order.status === 'pending_payment' ? 'En attente de paiement' :
                         order.status === 'paid' ? 'Payée' :
                         order.status === 'processing' ? 'En traitement' :
                         order.status === 'shipped' ? 'Expédiée' :
                         order.status === 'delivered' ? 'Livrée' :
                         order.status === 'cancelled' ? 'Annulée' : order.status}
                      </span>
                    </span>
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h3 className="text-lg font-semibold mb-2 text-white">Informations client</h3>
                    <div className="bg-white/5 p-4 rounded-md">
                      <p className="font-medium text-white">{order.customer_name}</p>
                      <p className="text-gray-200">{order.customer_email}</p>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2 text-white">Détails de paiement</h3>
                    <div className="bg-white/5 p-4 rounded-md">
                      <div className="flex justify-between items-center">
                        <span className="text-white">Statut&nbsp;:</span>
                        <Badge className={getStatusColor(order.status)}>
                          {order.status === 'pending_payment' ? 'En attente de paiement' :
                            order.status === 'paid' ? 'Payée' :
                            order.status === 'processing' ? 'En traitement' :
                            order.status === 'shipped' ? 'Expédiée' :
                            order.status === 'delivered' ? 'Livrée' :
                            order.status === 'cancelled' ? 'Annulée' :
                            order.status === 'inquiry' ? 'En attente d’infos' :
                            order.status}
                        </Badge>
                      </div>
                      <div className="flex justify-between mt-2">
                        <span className="text-white">Montant total:</span>
                        <span className="font-semibold text-white">{new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XAF' }).format(order.total_amount)}</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="mb-6">
  <h3 className="text-lg font-semibold mb-4 text-white">Gérer la commande</h3>
  <div className="mb-4">
    <label htmlFor="comment" className="block text-white text-sm font-medium mb-2">Commentaire interne:</label>
    <textarea
      id="comment"
      className="w-full p-2 bg-white/10 border border-white/20 rounded-md text-white"
      rows={2}
      value={comment}
      onChange={(e) => setComment(e.target.value)}
      placeholder="Ajouter un commentaire interne (optionnel)"
    />
  </div>
  
  {order.status === 'cancelled' ? (
    <p className="text-[#D4AF37] mt-2">Cette commande a été annulée. Aucune action n'est possible.</p>
  ) : order.status === 'inquiry' ? (
    <div className="flex flex-wrap gap-2 mt-2">
      <Button variant="destructive" className="bg-red-500/20 text-red-400 border-red-500/30" onClick={() => updateOrderStatus('cancelled')}>
        Annuler la commande
      </Button>
      <Button variant="outline" className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30" onClick={() => updateOrderStatus('pending_payment')}>
        Repasser en attente de paiement
      </Button>
      <Button variant="outline" className="bg-green-500/20 text-green-400 border-green-500/30" onClick={() => updateOrderStatus('paid')}>
        Marquer comme payée
      </Button>
      <Button variant="outline" className="bg-gray-500/20 text-gray-300 border-gray-500/30" onClick={() => deleteOrder()}>
        Supprimer définitivement
      </Button>
    </div>
  ) : order.payment_status !== 'paid' ? (
    <div className="flex flex-wrap gap-2 mt-2">
      <Button variant="destructive" className="bg-red-500/20 text-red-400 border-red-500/30" onClick={() => updateOrderStatus('cancelled')}>
        Annuler la commande
      </Button>
      <Button variant="outline" className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30" onClick={() => updateOrderStatus('inquiry')}>
        Mettre en attente d’informations
      </Button>
      <Button variant="outline" className="bg-green-500/20 text-green-400 border-green-500/30" onClick={() => updateOrderStatus('paid')}>
        Marquer comme payée
      </Button>
      <Button variant="outline" className="bg-gray-500/20 text-gray-300 border-gray-500/30" onClick={() => deleteOrder()}>
        Supprimer définitivement
      </Button>
    </div>
  ) : (
    <div className="flex flex-wrap gap-2">
      {order.status !== 'paid' && order.status !== 'processing' && (
        <Button 
          variant="outline" 
          onClick={() => updateOrderStatus('processing')}
          className="bg-blue-500/20 text-blue-500 hover:bg-blue-500/30 border-blue-500/30"
        >
          <Package className="mr-2 h-4 w-4" />
          Marquer en traitement
        </Button>
      )}
      {order.status !== 'shipped' && order.status !== 'delivered' && (
        <Button 
          variant="outline" 
          onClick={() => updateOrderStatus('shipped')}
          className="bg-purple-500/20 text-purple-500 hover:bg-purple-500/30 border-purple-500/30"
        >
          <Truck className="mr-2 h-4 w-4" />
          Marquer comme expédiée
        </Button>
      )}
      {order.status !== 'delivered' && (
        <Button 
          variant="outline" 
          onClick={() => updateOrderStatus('delivered')}
          className="bg-green-500/20 text-green-500 hover:bg-green-500/30 border-green-500/30"
        >
          <CheckCircle className="mr-2 h-4 w-4" />
          Marquer comme livrée
        </Button>
      )}
    </div>
  )}
</div>
                
                <div>
                  <h3 className="text-lg font-semibold mb-4 text-white">Articles commandés</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse text-white">
                      <thead>
                        <tr className="border-b border-white/10">
                          <th className="py-3 px-2 text-left font-semibold text-white">Image</th>
                          <th className="py-3 px-2 text-left font-semibold text-white">Produit</th>
                          <th className="py-3 px-2 text-center font-semibold text-white">Qté</th>
                          <th className="py-3 px-2 text-right font-semibold text-white">Prix unitaire</th>
                          <th className="py-3 px-2 text-right font-semibold text-white">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {order.items && order.items.map((item: OrderItem) => {
                          // Déterminer l'URL de l'image
                          const imageUrl = item.product?.images?.[0]?.image_url || item.image_url;
                          // Déterminer le titre du produit
                          const productTitle = item.product?.title || item.title || 'Produit';
                          // Déterminer le prix (utiliser price_at_purchase qui est toujours défini)
                          const price = item.price_at_purchase || 0;
                          
                          return (
                            <tr key={item.id} className="border-b border-white/5">
                              <td className="py-3 px-2 w-20">
                                {imageUrl && (
                                  <div className="w-16 h-16 rounded-md overflow-hidden">
                                    <img 
                                      src={imageUrl} 
                                      alt={productTitle} 
                                      className="w-full h-full object-cover"
                                    />
                                  </div>
                                )}
                              </td>
                              <td className="py-3 px-2">
                                <p className="font-medium">{productTitle}</p>
                              </td>
                              <td className="py-3 px-2 text-center">{item.quantity}</td>
                              <td className="py-3 px-2 text-right">
                                <p className="font-medium">
                                  {new Intl.NumberFormat('fr-FR').format(price)} FCFA
                                </p>
                              </td>
                              <td className="py-3 px-2 text-right">
                                <p className="font-semibold">
                                  {new Intl.NumberFormat('fr-FR').format(price * item.quantity)} FCFA
                                </p>
                              </td>
                            </tr>
                          );
                        })}
                        <tr>
                          <td colSpan={3}></td>
                          <td className="py-4 px-2 text-right font-semibold">Total:</td>
                          <td className="py-4 px-2 text-right">
                            <p className="text-lg font-bold">
                              {new Intl.NumberFormat('fr-FR').format(order.total_amount)} FCFA
                            </p>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Historique des statuts */}
            {order.history && order.history.length > 0 && (
              <Card className="mt-6 bg-white/5">
                <CardContent>
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-white">Historique des statuts</h3>
                    
                    {/* Barre de recherche et filtres */}
                    <div className="flex flex-col md:flex-row gap-3 mb-4">
                      <div className="relative flex-1">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Search className="h-4 w-4 text-white/50" />
                        </div>
                        <input
                          type="text"
                          placeholder="Rechercher dans les commentaires..."
                          className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-md text-white"
                          value={searchTerm}
                          onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setCurrentPage(1); // Réinitialiser la page lors d'une recherche
                          }}
                        />
                      </div>
                      <select
                        className="bg-white/10 border border-white/20 rounded-md text-white px-4 py-2"
                        value={statusFilter}
                        onChange={(e) => {
                          setStatusFilter(e.target.value);
                          setCurrentPage(1); // Réinitialiser la page lors d'un changement de filtre
                        }}
                      >
                        <option value="">Tous les statuts</option>
                        <option value="pending_payment">En attente de paiement</option>
                        <option value="paid">Payée</option>
                        <option value="processing">En traitement</option>
                        <option value="shipped">Expédiée</option>
                        <option value="delivered">Livrée</option>
                        <option value="cancelled">Annulée</option>
                        <option value="inquiry">En attente d'infos</option>
                      </select>
                    </div>
                    
                    {order.history && order.history.length > 0 ? (
                      <>
                        <div className="space-y-3">
                          {filteredHistory.map((entry, index) => (
                            <div key={index} className="bg-white/5 p-3 rounded-md">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center">
                                  <Badge className={getStatusColor(entry.new_status)}>
                                    {entry.new_status === 'pending_payment' ? 'En attente de paiement' :
                                      entry.new_status === 'paid' ? 'Payée' :
                                      entry.new_status === 'processing' ? 'En traitement' :
                                      entry.new_status === 'shipped' ? 'Expédiée' :
                                      entry.new_status === 'delivered' ? 'Livrée' :
                                      entry.new_status === 'cancelled' ? 'Annulée' :
                                      entry.new_status === 'inquiry' ? 'En attente d\'infos' :
                                      entry.new_status}
                                  </Badge>
                                  <span className="text-white/70 text-sm ml-3">
                                    {new Date(entry.date).toLocaleDateString('fr-FR')} à {new Date(entry.date).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                  </span>
                                </div>
                                <span className="text-white/50 text-sm">Par: {entry.user_name || entry.user_id || 'Admin'}</span>
                              </div>
                              {entry.comment && (
                                <div className="mt-2 text-white/80 text-sm bg-white/10 p-2 rounded">
                                  {entry.comment}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                        
                        {/* Pagination */}
                        {Math.ceil((filteredAndSortedHistory?.length || 0) / itemsPerPage) > 1 && (
                          <div className="flex justify-between items-center mt-4">
                            <div className="text-sm text-white/70">
                              Affichage {(currentPage - 1) * itemsPerPage + 1} à {Math.min(currentPage * itemsPerPage, filteredAndSortedHistory?.length || 0)} sur {filteredAndSortedHistory?.length || 0}
                            </div>
                            <div className="flex gap-2">
                              <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                                className="bg-white/10 border-white/20 text-white hover:bg-white/20"
                              >
                                <ChevronLeft className="h-4 w-4" />
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil((filteredAndSortedHistory?.length || 0) / itemsPerPage)))}
                                disabled={currentPage >= Math.ceil((filteredAndSortedHistory?.length || 0) / itemsPerPage)}
                                className="bg-white/10 border-white/20 text-white hover:bg-white/20"
                              >
                                <ChevronRight className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <p className="text-white/50">Aucun historique disponible</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        ) : (
          <Card className="bg-card border-white/5">
            <CardContent className="pt-6 text-center py-12">
              <AlertTriangle className="h-12 w-12 mx-auto text-yellow-500 mb-4" />
              <h2 className="text-xl font-semibold mb-2">Commande introuvable</h2>
              <p className="text-white/70 mb-4">
                Impossible de trouver les détails de cette commande
              </p>
              <Button onClick={() => navigate('/admin/boutique/commandes')}>
                Retour aux commandes
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminOrderDetails;
